import { useCallback, useState } from "react";
import {
  AnalysisParametersDto,
  UniqueFieldValuesDto,
} from "@green0meter/api-client";

import {
  useGetPaygapSettings,
  useUpdatePaygapSettings,
  useGetUniqueFieldValues,
} from "@api/hooks/paygap";
import { useUserStore } from "@store/user";

/**
 * Helper to compare arrays of items by their name property
 */
const arraysEqual = <T extends { name: string }>(
  a: T[] | undefined | null,
  b: T[] | undefined | null
): boolean => {
  const aItems = a ?? [];
  const bItems = b ?? [];

  if (aItems.length !== bItems.length) return false;

  const aNames = new Set(aItems.map((item) => item.name));
  const bNames = new Set(bItems.map((item) => item.name));

  if (aNames.size !== bNames.size) return false;

  for (const name of aNames) {
    if (!bNames.has(name)) return false;
  }

  return true;
};

/**
 * Check if all referential fields in analysis parameters are empty
 */
const areReferentialsEmpty = (
  params: AnalysisParametersDto | null | undefined
): boolean => {
  return (
    (!params?.educationAndTraining || params.educationAndTraining.length === 0) &&
    (!params?.professionalPositions || params.professionalPositions.length === 0) &&
    (!params?.grading1 || params.grading1.length === 0) &&
    (!params?.grading2 || params.grading2.length === 0) &&
    (!params?.grading3 || params.grading3.length === 0)
  );
};

/**
 * Check if any education items are missing the numberOfYears field
 */
const hasEducationMissingYears = (
  params: AnalysisParametersDto | null | undefined
): boolean => {
  if (!params?.educationAndTraining?.length) return false;

return params.educationAndTraining.some(
    (item) => !item.numberOfYears || item.numberOfYears.trim() === ""
  );
};

/**
 * Check if unique values have any data
 */
const hasUniqueValues = (values: UniqueFieldValuesDto | null | undefined): boolean => {
  if (!values) return false;

  return (
    (values.educationAndTraining?.length ?? 0) > 0 ||
    (values.professionalPositions?.length ?? 0) > 0 ||
    (values.grading1?.length ?? 0) > 0 ||
    (values.grading2?.length ?? 0) > 0 ||
    (values.grading3?.length ?? 0) > 0
  );
};

/**
 * Check if existing params differ from new unique values
 */
const paramsDiffer = (
  existing: AnalysisParametersDto | null | undefined,
  newValues: UniqueFieldValuesDto | null | undefined
): boolean => {
  if (!newValues) return false;

  return (
    !arraysEqual(existing?.educationAndTraining, newValues.educationAndTraining) ||
    !arraysEqual(existing?.professionalPositions, newValues.professionalPositions) ||
    !arraysEqual(existing?.grading1, newValues.grading1) ||
    !arraysEqual(existing?.grading2, newValues.grading2) ||
    !arraysEqual(existing?.grading3, newValues.grading3)
  );
};

export interface UseAutoPopulateAnalysisParamsResult {
  /**
   * Check employee data and populate analysis params if needed.
   * Returns true if dialog needs to be shown (user confirmation required).
   * Returns false if params were auto-populated or no action needed.
   */
  checkAndPopulate: () => Promise<boolean>;

  /**
   * Whether the overwrite confirmation dialog should be shown
   */
  showOverwriteDialog: boolean;

  /**
   * Close the overwrite dialog without applying changes
   */
  closeOverwriteDialog: () => void;

  /**
   * The pending unique values that would be applied
   */
  pendingParams: UniqueFieldValuesDto | null;

  /**
   * The existing analysis parameters
   */
  existingParams: AnalysisParametersDto | null | undefined;

  /**
   * Confirm overwrite and apply the pending params
   */
  confirmOverwrite: () => Promise<void>;

  /**
   * Whether an update operation is in progress
   */
  isUpdating: boolean;

  /**
   * Whether the missing years dialog should be shown
   */
  showMissingYearsDialog: boolean;

  /**
   * Close the missing years dialog
   */
  closeMissingYearsDialog: () => void;

  /**
   * Check if education items have missing years and show dialog if needed.
   * Returns true if dialog is shown, false if all years are filled.
   * Async to ensure fresh data is fetched after any recent updates.
   */
  checkForMissingYears: () => Promise<boolean>;
}

/**
 * Hook to auto-populate analysis parameters from employee data.
 *
 * Logic:
 * 1. If DB referentials are empty → write directly (no popup)
 * 2. If DB has values AND they differ → show confirmation popup
 * 3. If DB has same values → no action needed
 */
export const useAutoPopulateAnalysisParams = (): UseAutoPopulateAnalysisParamsResult => {
  const { user } = useUserStore();

  const { settings, refetchPaygapSettings } = useGetPaygapSettings(user, true);
  const { uniqueFieldValues, refetchUniqueFieldValues } = useGetUniqueFieldValues(
    user,
    true
  );
  const { updateSettings, isPendingUpdateSettings } = useUpdatePaygapSettings(user);

  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [pendingParams, setPendingParams] = useState<UniqueFieldValuesDto | null>(null);
  const [showMissingYearsDialog, setShowMissingYearsDialog] = useState(false);

  const checkAndPopulate = useCallback(async (): Promise<boolean> => {
    // Refetch to get the latest data
    await refetchUniqueFieldValues();
    await refetchPaygapSettings();

    // Get fresh data from refetch results - use current state
    const freshUniqueValues = uniqueFieldValues;
    const freshSettings = settings;

    // No unique values from employee data - nothing to populate
    if (!hasUniqueValues(freshUniqueValues)) {
      return false;
    }

    const existingParams = freshSettings?.analysisParameters;

    // Case 1: Existing params are empty - write directly
    if (areReferentialsEmpty(existingParams)) {
      return new Promise<boolean>((resolve) => {
        updateSettings(
          {
            analysisParameters: {
              ...existingParams,
              educationAndTraining: freshUniqueValues?.educationAndTraining,
              professionalPositions: freshUniqueValues?.professionalPositions,
              grading1: freshUniqueValues?.grading1,
              grading2: freshUniqueValues?.grading2,
              grading3: freshUniqueValues?.grading3,
            },
          },
          {
            onSuccess: () => resolve(false),
            onError: () => resolve(false),
          }
        );
      });
    }

    // Case 2: Params differ - show confirmation dialog
    if (paramsDiffer(existingParams, freshUniqueValues)) {
      setPendingParams(freshUniqueValues ?? null);
      setShowOverwriteDialog(true);

return true;
    }

    // Case 3: Params are the same - no action needed
    return false;
  }, [
    uniqueFieldValues,
    settings,
    updateSettings,
    refetchUniqueFieldValues,
    refetchPaygapSettings,
  ]);

  const closeOverwriteDialog = useCallback((): void => {
    setShowOverwriteDialog(false);
    setPendingParams(null);
  }, []);

  const confirmOverwrite = useCallback(async (): Promise<void> => {
    if (!pendingParams) {
      closeOverwriteDialog();

return;
    }

    return new Promise<void>((resolve) => {
      updateSettings(
        {
          analysisParameters: {
            ...settings?.analysisParameters,
            educationAndTraining: pendingParams.educationAndTraining,
            professionalPositions: pendingParams.professionalPositions,
            grading1: pendingParams.grading1,
            grading2: pendingParams.grading2,
            grading3: pendingParams.grading3,
          },
        },
        {
          onSuccess: () => {
            closeOverwriteDialog();
            resolve();
          },
          onError: () => {
            closeOverwriteDialog();
            resolve();
          },
        }
      );
    });
  }, [pendingParams, settings?.analysisParameters, updateSettings, closeOverwriteDialog]);

  const closeMissingYearsDialog = useCallback((): void => {
    setShowMissingYearsDialog(false);
  }, []);

  const checkForMissingYears = useCallback(async (): Promise<boolean> => {
    // Refetch to get latest data after any updates
    const { data: freshSettings } = await refetchPaygapSettings();

    if (hasEducationMissingYears(freshSettings?.analysisParameters)) {
      setShowMissingYearsDialog(true);

return true;
    }

return false;
  }, [refetchPaygapSettings]);

  return {
    checkAndPopulate,
    showOverwriteDialog,
    closeOverwriteDialog,
    pendingParams,
    existingParams: settings?.analysisParameters,
    confirmOverwrite,
    isUpdating: isPendingUpdateSettings,
    showMissingYearsDialog,
    closeMissingYearsDialog,
    checkForMissingYears,
  };
};
